import React from "react";
import {
  ShieldCheck,
  Database,
  Cloud,
  Network,
  Briefcase,
  Lightbulb,
  ArrowUpRight,
} from "lucide-react";

const SERVICES = [
  {
    id: "cybersecurite",
    icon: ShieldCheck,
    title: "Cybersécurité",
    pitch: "Protéger ce qui compte.",
    desc: "Audit de sécurité, SOC managé, conformité ISO 27001 / RGPD, sensibilisation et réponse à incident.",
    bullets: ["Audit & Pentest", "SOC 24/7", "Conformité & RGPD"],
  },
  {
    id: "data",
    icon: Database,
    title: "Data & PostgreSQL",
    pitch: "Données fiables, performances durables.",
    desc: "Architecture data, modélisation, optimisation PostgreSQL, ETL, data warehouse et BI.",
    bullets: ["Architecture & modélisation", "Tuning PostgreSQL", "ETL & BI"],
  },
  {
    id: "infrastructure",
    icon: Cloud,
    title: "Infrastructure & Cloud",
    pitch: "Des infrastructures résilientes et évolutives.",
    desc: "Migration cloud, hybride, virtualisation, sauvegarde et plan de reprise d'activité.",
    bullets: ["Azure · AWS · GCP", "Virtualisation", "PRA / PCA"],
  },
  {
    id: "integration",
    icon: Network,
    title: "Intégration de systèmes",
    pitch: "Connecter, fluidifier, performer.",
    desc: "APIs, middleware, ESB, interopérabilité entre vos applications métier.",
    bullets: ["APIs & Webhooks", "ESB / Middleware", "Interopérabilité"],
  },
  {
    id: "conseil",
    icon: Briefcase,
    title: "Conseil IT",
    pitch: "Stratégie, expertise et accompagnement.",
    desc: "Schéma directeur, gouvernance SI, conduite du changement et coaching DSI.",
    bullets: ["Schéma directeur", "Gouvernance SI", "Coaching DSI"],
  },
  {
    id: "innovation",
    icon: Lightbulb,
    title: "Innovation",
    pitch: "Imaginer aujourd'hui les solutions de demain.",
    desc: "IA générative, automatisation, R&D produit, design sprints et POCs accélérés.",
    bullets: ["IA & Automatisation", "R&D Produit", "Design Sprints"],
  },
];

function ServiceCard({ s }) {
  const Icon = s.icon;
  return (
    <article
      data-testid={`service-card-${s.id}`}
      className="group hk-card relative bg-[#142042]/60 border border-[#D4AF37]/15 p-6 sm:p-7 md:p-9 rounded-[2px] flex flex-col h-full"
    >
      <div className="absolute top-0 left-0 right-0 h-[2px] origin-left scale-x-0 group-hover:scale-x-100 transition-transform duration-500 bg-[#D4AF37]" />
      <div className="flex items-start justify-between">
        <div className="inline-flex items-center justify-center w-12 h-12 border border-[#D4AF37]/25 rounded-[2px] text-[#D4AF37]">
          <Icon size={22} strokeWidth={1.6} />
        </div>
        <ArrowUpRight
          size={20}
          className="text-[#F5F5F5]/25 group-hover:text-[#D4AF37] transition-colors"
        />
      </div>

      <div className="mt-6">
        <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight text-[#F5F5F5]">
          {s.title}
        </h3>
        <div className="font-display text-[#D4AF37] text-sm mt-1 italic font-medium">
          {s.pitch}
        </div>
      </div>
      <p className="mt-4 text-[14px] text-[#F5F5F5]/65 leading-relaxed max-w-md">
        {s.desc}
      </p>

      <ul className="mt-6 space-y-2 text-sm text-[#F5F5F5]/75">
        {s.bullets.map((b) => (
          <li key={b} className="flex items-center gap-2">
            <span className="inline-block w-1.5 h-1.5 bg-[#D4AF37]" />
            {b}
          </li>
        ))}
      </ul>
    </article>
  );
}

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="bg-[#0A1326] py-20 md:py-32 relative"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-5 md:px-8 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 md:mb-14">
          <div>
            <div className="overline text-[#D4AF37]">── Nos Expertises</div>
            <h2 className="font-display text-[32px] sm:text-4xl md:text-5xl lg:text-6xl font-black tracking-tight mt-4 max-w-3xl text-[#F5F5F5] leading-[1.05]">
              Six pôles d'expertise, une seule promesse&nbsp;:{" "}
              <span className="text-[#D4AF37]">l'excellence</span>.
            </h2>
          </div>
          <p className="text-[#F5F5F5]/65 max-w-md text-[14px] sm:text-[15px] leading-relaxed">
            De la stratégie à l'exploitation — nous bâtissons des SI sûrs,
            performants et alignés sur vos enjeux métier.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s) => (
            <ServiceCard key={s.id} s={s} />
          ))}
        </div>
      </div>
    </section>
  );
}
